import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthModal, useIsMobile } from "senf-atomic-design-system";
import { useModals } from "senf-atomic-design-system/src/components/molecules/modalStack/ModalProvider";
import { useAuthContext } from "senf-shared";
import Survey from "../pages/Survey";
import { likeIdea, unlikeIdea } from "../redux/actions/likeActions";
import { setProject } from "../redux/actions/projectActions";
import type { ClientState } from "../redux/reducers/dataReducer";
import { RESET_PROFILE_PAGE, SET_ERRORS, SET_IDEA } from "../redux/types";

export const useHandles = (
  projectId: string,
  ideaMapId: string,
  ideaId: string,
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shouldSnapToTop, setShouldSnapToTop] = useState(false);
  const [pendingLikeIdeaId, setPendingLikeIdeaId] = useState<string | null>(
    null,
  );

  const { openModal, closeDrawer, closeAllModals } = useModals();
  const { user, signOut, handleLike: userHandleLike } = useAuthContext();
  const project = useSelector((state: ClientState) => state.data.project);
  const { openAuthModal, handleCheckAuth } = useAuthModal(project);

  const isUserProfileComplete =
    user?.profileCompleted ||
    (user?.userId && project?.settings?.demographicData?.collect === false);
  const isUserProfileToBeCompleted =
    user?.userId &&
    project?.settings?.demographicData?.collect !== false &&
    !user?.profileCompleted;

  // Effect to handle post-login actions when user state changes
  // useEffect(() => {
  //   if (pendingLikeIdeaId && user?.userId) {
  //     // User has been authenticated, check if we need to show profile completion
  //     if (
  //       project?.settings?.demographicData?.collect !== false &&
  //       !user?.profileCompleted
  //     ) {
  //       // Store the idea ID before clearing it
  //       const currentIdeaId = pendingLikeIdeaId;
  //       console.log("liking 1");
  //       // Clear pending immediately after opening modal to prevent infinite loop
  //       setPendingLikeIdeaId(null);
  //       // Show authAddDetails modal for profile completion
  //       openAuthModal("authAddDetails", {
  //         id: "authModal",
  //         onAfterSave: () => {
  //           // The modal needs to be closed explicitly since we're overriding the default success behavior
  //           closeModal();
  //           handleLike(project, currentIdeaId);
  //         },
  //       });
  //     } else {
  //       // Profile is complete or not required, proceed with like
  //       handleLike(project, pendingLikeIdeaId);
  //       setPendingLikeIdeaId(null);
  //     }
  //   }
  // }, [
  //   user?.userId,
  //   user?.profileCompleted,
  //   pendingLikeIdeaId,
  //   project?.settings?.demographicData?.collect,
  // ]);

  const setAuthEditOpen = useCallback(() => {
    if (!user?.userId) {
      // Note: authVerifyEmail is no longer supported, fallback to login
      openAuthModal("login", { variant: "other" });
      return;
    }
    openAuthModal("authAddDetails", {
      id: "authModal",
      onAfterSave: () => {
        closeDrawer({ id: "authModal" });
      },
    });
  }, [openAuthModal, user?.authenticated, user?.emailVerified]);

  const login = useCallback(() => {
    if (!user?.authenticated) {
      openAuthModal("login");
    }
  }, [openAuthModal, user?.authenticated]);

  const loginInfoPage = useCallback(() => {
    if (!user?.authenticated) {
      openAuthModal("login", { variant: "infoPage" });
    }
  }, [openAuthModal, user?.authenticated]);

  const logout = useCallback(() => {
    const openDiscardModal = () => {
      openModal(null, {
        title: t("global:guestLogout"),
        description: t("global:guestLogoutDescription"),
        swipe: !!isMobile,
        size: isMobile ? "full" : "md",
        cancelText: t("global:cancel"),
        submitText: t("auth:logout"),
        critical: true,
        onSubmit: () => {
          signOut().then(() => {
            dispatch({ type: RESET_PROFILE_PAGE });
          });
        },
      });
    };
    if (user.isAnonymous) {
      openDiscardModal();
      return;
    }
    signOut().then(() => {
      dispatch({ type: RESET_PROFILE_PAGE });
      // openAuthModal("login");
    });
  }, [signOut, dispatch, openAuthModal]);

  const openMyAccount = useCallback(() => {
    if (!user?.authenticated) {
      openAuthModal("login", { variant: "other" });
      return;
    }
    if (!user?.authenticated && !user?.emailVerified) {
      // Note: authVerifyEmail is no longer supported, fallback to login
      openAuthModal("login", { variant: "other" });
      return;
    }

    navigate(`?profile=${user?.userId}`);
  }, [navigate, user?.userId, ideaId, projectId, openAuthModal, openModal]);

  const openFeedback = useCallback(
    (type) => {
      let surveyId;
      if (type === "general") {
        surveyId = "ro8bJTitxE767COIQzR4";
      }
      if (type === "addIdea") {
        surveyId = "i9NOQh1dcVp8fSxyr7Od";
      }

      openModal(
        <Survey
          organizationId={"k5XMGl8qMSCDHyjmKDYg"}
          projectId={"uCdAXAtpRKMA2lPTyzrT"}
          surveyId={surveyId}
          repeatable={true}
        />,
        {
          id: "survey",
          swipe: !!isMobile,
          size: isMobile && "fullscreen",
          transform: false,
        },
      );
    },
    [isMobile],
  );

  const openCard = useCallback(
    (event, cardType, cardId) => {
      if (cardType === "ideaCard") {
        // trackId
        // window?.umami?.("list_openIdeaClick");
        const rootProfileId = searchParams.get("profile");
        if (rootProfileId) {
          navigate(
            `/projects/${projectId}/ideaMaps/${ideaMapId}/${cardId}?rootProfileId=${rootProfileId}`,
          );
        } else {
          navigate(`/projects/${projectId}/ideaMaps/${ideaMapId}/${cardId}`);
        }
      } else if (cardType === "projectCard") {
        navigate(`/projects/${cardId}/info`);
      }
    },
    [projectId],
  );

  const handleLike = async (project, ideaId, userParam) => {
    try {
      // Ensure we have a valid user object with userId
      let userToUse = userParam || user;

      // If the passed user doesn't have userId but the context user does, use the context user
      if (!userToUse?.userId && user?.userId) {
        userToUse = user;
      }

      if (!userToUse?.userId) {
        console.error(
          "No valid user with userId available for liking. User object:",
          userToUse,
        );
        throw new Error("No valid user with userId available for liking");
      }

      const like = await userHandleLike(ideaId);
      const action = like ? likeIdea : unlikeIdea;
      await dispatch(action(project, ideaMapId, ideaId, userToUse));
      return like;
    } catch (error) {
      dispatch({
        type: SET_ERRORS,
        payload: {
          message: error.message,
        },
      });
      console.log("error", error);

      return false;
    }
  };

  const buttonLike = (event, ideaId) => {
    event.stopPropagation();
    // user may like
    if (isUserProfileComplete) {
      return new Promise((resolve) => {
        setTimeout(() => {
          handleLike(project, ideaId, user).then(resolve);
        }, 100);
      });
    }

    // If user is not authenticated, check if auto-login should happen
    if (!user?.userId) {
      // Check if auto-login conditions are met (same as in useAuthModal)
      if (project?.settings?.guestLogin !== false) {
        // Auto-login should happen - set pending idea and let auto-login handle it
        setPendingLikeIdeaId(ideaId);
        return new Promise((resolve) => {
          // For auto-login, we need to handle demographics after login
          openAuthModal("login", {
            variant: "like",
            loggingInCallback: (updatedUser) => {
              // After login, check if demographics are needed
              if (
                project?.settings?.demographicData?.collect !== false &&
                !updatedUser?.profileCompleted
              ) {
                openAuthModal("authAddDetails", {
                  id: "authModal",
                  onAfterSave: (finalUser) => {
                    setTimeout(() => {
                      handleLike(project, ideaId, finalUser).then((result) => {
                        closeDrawer({ id: "authModal" });
                        resolve(result);
                      });
                    }, 100);
                  },
                  onClose: () => {
                    resolve(false);
                  },
                });
              } else {
                setTimeout(() => {
                  handleLike(project, ideaId, updatedUser).then(resolve);
                }, 100);
              }
            },
            onClose: () => {
              resolve(false);
              closeDrawer({ id: "authModal" });
            },
          });
        });
      }

      // Manual login required
      return new Promise((resolve) => {
        if (isUserProfileToBeCompleted) {
          openAuthModal("authAddDetails", {
            id: "authModal",
            onAfterSave: (updatedUser) => {
              // Add a small delay to ensure the user context is updated
              setTimeout(() => {
                handleLike(project, ideaId, updatedUser).then((result) => {
                  closeDrawer({ id: "authModal" });
                  resolve(result);
                });
              }, 100);
            },
            onClose: () => {
              // If user cancels the demographics modal, resolve with false (no like action)
              resolve(false);
            },
          });
        } else {
          openAuthModal("login", {
            variant: "like",
            loggingInCallback: () => {
              setTimeout(() => {
                handleLike(project, ideaId, user).then(resolve);
              }, 100);
            },
            onClose: () => {
              resolve(false);
              closeDrawer({ id: "authModal" });
            },
          });
        }
      });
    }

    // User is authenticated but profile incomplete
    if (isUserProfileToBeCompleted) {
      return new Promise((resolve) => {
        openAuthModal("authAddDetails", {
          id: "authModal",
          onAfterSave: (updatedUser) => {
            // closeModal({ id: "authModal" });
            // Add a small delay to ensure the user context is updated
            setTimeout(() => {
              handleLike(project, ideaId, updatedUser).then((result) => {
                // closeModal({ id: "authModal" });
                closeDrawer({ id: "authModal" });
                resolve(result);
              });
            }, 100);
          },
          onClose: () => {
            // If user cancels the demographics modal, resolve with false (no like action)
            resolve(false);
          },
        });
      });
    }
    // This should never be reached, but if it is, return a resolved promise
    return Promise.resolve();
  };

  // const handlePostLogin = (loggedInUser, ideaId) => {
  //   // After successful manual login, check if profile needs completion
  //   if (
  //     loggedInUser?.userId &&
  //     project?.settings?.demographicData?.collect !== false &&
  //     !loggedInUser?.profileCompleted
  //   ) {
  //     console.log("liking 3");

  //     openAuthModal("authAddDetails", {
  //       id: "authModal",
  //       onAfterSave: () => {
  //         handleLike(project, ideaId);
  //         setPendingLikeIdeaId(null); // Clear pending
  //       },
  //     });
  //   } else {
  //     handleLike(project, ideaId);
  //     setPendingLikeIdeaId(null); // Clear pending
  //   }
  // };
  const buttonComment = (event, currentIdeaId) => {
    event.stopPropagation();
    // handleCheckAuth({ checkAuthAddDetails: true });

    if (!user?.userId) {
      openAuthModal("login", {
        variant: "comment",
        onClose: () => {
          closeDrawer({ id: "authModal" });
        },
      });
    }
    if (isUserProfileToBeCompleted) {
      openAuthModal("authAddDetails", {
        id: "authModal",
        onClose: () => {
          closeDrawer({ id: "authModal" });
        },
      });
      return;
    }

    if (!ideaId) {
      navigate(`${currentIdeaId}`);
    }

    setShouldSnapToTop(false);
    setTimeout(() => {
      setShouldSnapToTop(true);
    }, 1100);

    if (isMobile) {
      setTimeout(() => {
        document.getElementById("ideaDetailPage.commentInput")?.focus();
      }, 1500);
    } else {
      setTimeout(() => {
        document.getElementById("ideaDetailPage.commentInput")?.focus();
      }, 500);
    }
  };

  const clickIdeaMarker = (id) => {
    dispatch({ type: SET_IDEA, payload: null });

    navigate(`/projects/${projectId}/ideaMaps/${ideaMapId}/${id}`);

    // trackId
    // window?.umami?.("map_openIdeaClick");
  };

  const openAddIdea = useCallback(
    (ideaMap) => {
      if (ideaMap) {
        closeAllModals();
        navigate(
          `/projects/${project.projectId}/ideaMaps/${
            ideaMap?.ideaMapId || ideaMapId
          }/addIdea`,
        );
        return;
      }
      if (pathname.indexOf("editIdea") > -1) {
        navigate(`${pathname.split("/").slice(0, -1).join("/")}`);

        return;
      }
      navigate(
        `/projects/${project.projectId}/ideaMaps/${
          ideaMap.ideaMapId || ideaMapId
        }`,
      );
    },
    [pathname],
  );

  const openInfo = () => {
    navigate(`/projects/${project.projectId}/info`);
    closeAllModals();
  };
  const openMap = (ideaMap) => {
    navigate(`/projects/${project.projectId}/ideaMaps/${ideaMap.ideaMapId}`);
  };

  const openSurvey = (surveyId: string) => {
    closeAllModals();

    navigate(`/projects/${project.projectId}/survey/${surveyId}`);
  };

  const refetchData = useCallback(
    (ideaId: string) => {
      if (ideaId && project) {
        dispatch(setProject(project.projectId, navigate));
      }
    },
    [dispatch, project, navigate],
  );

  return {
    setAuthEditOpen,
    login,
    loginInfoPage,
    logout,
    openMyAccount,
    openFeedback,
    openCard,
    buttonLike,
    buttonComment,
    clickIdeaMarker,
    openAddIdea,
    openInfo,
    openMap,
    openSurvey,
    shouldSnapToTop,
    setShouldSnapToTop,
    refetchData,
  };
};

export default useHandles;
